import { TActionPlan } from '@valuecase/common'
import { CircularProgressIndicator } from '../../../components/legacy/CircularProgressIndicator/CircularProgressIndicator'
import { useMemo } from 'react'

interface ActionPlanCircularProgressProps {
  actionPlan: TActionPlan
  size?: number
  strokeWidth?: number
  className?: string
}

/**
 * A reusable component that displays the progress of an action plan using a circular indicator
 */
const ActionPlanCircularProgress = ({
  actionPlan,
  size = 8,
  strokeWidth = 4,
  className,
}: ActionPlanCircularProgressProps) => {
  /**
   * Calculate the number of completed tasks and total tasks
   */
  const { tasksCompleted, totalTasks } = useMemo(() => {
    const allTasks = actionPlan.milestones.flatMap((milestone) => milestone.tasks)
    const tasksCompleted = allTasks.filter((task) => task.checked && task.completedAt).length
    const totalTasks = allTasks.length
    return { tasksCompleted, totalTasks }
  }, [actionPlan.milestones])

  /**
   * Calculate the completion percentage
   */
  const completionPercentage = useMemo(() => {
    if (totalTasks === 0) return 0
    return (tasksCompleted / totalTasks) * 100
  }, [tasksCompleted, totalTasks])

  /**
   * Generate the options for the CircularProgressIndicator
   */
  const progressOptions = useMemo(() => {
    return {
      secondaryColor: 'var(--theme-grey-s3)',
      size,
      strokeWidth,
    }
  }, [size, strokeWidth])

  return (
    <div className={className}>
      <CircularProgressIndicator
        color={'var(--theme-success-s5)'}
        options={progressOptions}
        percentage={completionPercentage}
      />
    </div>
  )
}

export default ActionPlanCircularProgress
